/**
 * Shared Book Value Report — Public Page
 *
 * Read-only render of a Book Value Report snapshot. Token-validated, no auth.
 * Visual conventions match SharedBrokerReportPage (dark theme, VALUTTEN logo
 * with mixBlendMode:'lighten', SectionHeading + MetricCard primitives, brand
 * blue accents — NOT emerald, NOT slate-indigo).
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Loader2, AlertCircle, ShieldCheck, ShieldAlert } from 'lucide-react';
import { formatCompactCurrency } from '../utils/formatters';

type BrokerBucket = 'melissa_retained' | 'melissa_new' | 'nathan' | 'di_excluded' | 'unattributed';
type ReconciliationStatus = 'matched' | 'mismatch' | 'no_data';

interface BookValueBucketTotal {
  bucket: BrokerBucket;
  displayName: string;
  loanCount: number;
  clientCount: number;
  loanBookCents: number;
}

interface MonthlyBucketBreakdown {
  month: string;
  melissaRetainedCents: number;
  melissaNewCents: number;
  nathanCents: number;
  visibleTotalCents: number;
  diExcludedCents: number;
}

interface ReconciliationGate {
  perLoanTotalCents: number;
  monthlyAggregateTotalCents: number;
  driftCents: number;
  unattributedCents: number;
  unattributedLoanCount: number;
  unattributedThresholdCents: number;
  status: ReconciliationStatus;
}

interface BookValueLoanRow {
  loanId: string;
  brokerDisplay: string | null;
  settlementDate: string | null;
  loanBookCents: number;
  templateId: string | null;
  lender: string | null;
  client: string | null;
  bucket: BrokerBucket;
}

interface BookValueReportData {
  periodMonth: string;
  generatedAt: string;
  companyName: string;
  parentGroupName: string;
  buckets: BookValueBucketTotal[];
  visibleTotalCents: number;
  visibleLoanCount: number;
  visibleClientCount: number;
  monthlyTrend: MonthlyBucketBreakdown[];
  loans: BookValueLoanRow[];
  reconciliation: ReconciliationGate;
  ruleSetHash: string;
}

interface ShareResponse {
  shareId: string;
  companyName: string;
  periodMonth: string;
  createdAt: string;
  data: BookValueReportData;
}

const formatCurrency = (cents: number): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(cents / 100);

// Cards: the app-wide three-significant-figure rule. Tables keep formatCurrency.
const formatCurrencyCompact = (cents: number): string => formatCompactCurrency(cents);

const formatPeriod = (periodMonth: string): string => {
  const [y, m] = periodMonth.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
};

const formatMonthShort = (month: string): string => {
  const [y, m] = month.split('-');
  return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
};

const SharedBookValueReportPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [response, setResponse] = useState<ShareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeBucketTab, setActiveBucketTab] = useState<BrokerBucket>('melissa_retained');

  useEffect(() => {
    const fetch = async () => {
      if (!token) { setError('No share token provided'); setIsLoading(false); return; }
      try {
        const callable = httpsCallable<{ token: string }, ShareResponse>(
          functions, 'validateBookValueShareToken',
        );
        const r = await callable({ token });
        setResponse(r.data);
      } catch (e: any) {
        console.error('Book value share validation failed:', e);
        setError(e?.message || 'Failed to load shared report');
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [token]);

  const visibleBuckets = useMemo(
    () => response?.data.buckets.filter((b) => b.bucket !== 'di_excluded' && b.bucket !== 'unattributed') ?? [],
    [response],
  );
  const diExcluded = useMemo(
    () => response?.data.buckets.find((b) => b.bucket === 'di_excluded'),
    [response],
  );
  const unattributed = useMemo(
    () => response?.data.buckets.find((b) => b.bucket === 'unattributed'),
    [response],
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto text-blue-400 animate-spin mb-4" />
          <p className="text-gray-400">Loading shared report…</p>
        </div>
      </div>
    );
  }

  if (error || !response) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-4">
        <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl shadow-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-rose-400 mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Unable to load report</h1>
          <p className="text-gray-400 mb-4">{error || 'This share link is invalid, expired, or has been revoked.'}</p>
          <p className="text-sm text-gray-500">If you believe this is an error, please contact your VALUTTEN administrator.</p>
        </div>
      </div>
    );
  }

  const { data } = response;
  const reconOk = data.reconciliation.status === 'matched';

  return (
    <>
      <style>{`@media print { body { display: none !important; } }`}</style>

      <div className="min-h-screen bg-[#0f1117] text-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-8 space-y-6">

          {/* Header — VALUTTEN logo + report meta */}
          <div className="relative pb-5 border-b-2 border-blue-500">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <img
                src="/valutten-logo.jpg"
                alt="VALUTTEN"
                className="h-12"
                style={{ mixBlendMode: 'lighten' }}
              />
              <div className="text-right">
                <span className="inline-block bg-white/5 border border-white/10 rounded-md px-2.5 py-1 text-[11px] uppercase tracking-widest text-gray-400 mb-1">
                  Confidential Report
                </span>
                <div className="text-sm text-gray-500">{formatPeriod(data.periodMonth)}</div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mt-4">
              {data.companyName} — Book Value Report
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {data.parentGroupName
                ? `Prepared for ${data.parentGroupName}`
                : 'Per-broker outstanding loan book balances'}
            </p>
          </div>

          {/* Executive Summary — 4 metric cards */}
          <section>
            <SectionHeading title="Summary" period={formatPeriod(data.periodMonth)} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard
                hero
                label="Total Loan Book"
                value={formatCurrencyCompact(data.visibleTotalCents)}
                sub={`${data.visibleLoanCount.toLocaleString('en-AU')} loans · ${data.visibleClientCount.toLocaleString('en-AU')} clients`}
                color="#3b82f6"
              />
              {visibleBuckets.map((b) => (
                <MetricCard
                  key={b.bucket}
                  label={b.displayName}
                  value={formatCurrencyCompact(b.loanBookCents)}
                  sub={`${b.loanCount.toLocaleString('en-AU')} loans`}
                  color={
                    b.bucket === 'melissa_retained' ? '#8b5cf6'
                    : b.bucket === 'melissa_new' ? '#f59e0b'
                    : '#10b981'
                  }
                />
              ))}
            </div>
            {diExcluded && diExcluded.loanCount > 0 && (
              <div className="mt-3 px-4 py-2.5 bg-gray-800/30 border border-white/[0.04] border-dashed rounded-lg text-xs text-gray-400">
                <span className="text-gray-500">⊘</span>{' '}
                <strong className="text-gray-300">Excluded from book total:</strong>{' '}
                {diExcluded.displayName} ({diExcluded.loanCount} {diExcluded.loanCount === 1 ? 'loan' : 'loans'} · {formatCurrency(diExcluded.loanBookCents)})
              </div>
            )}
          </section>

          {/* Monthly trend — stacked area chart */}
          <section>
            <SectionHeading
              title="Book Value Trend"
              period={`${formatMonthShort(data.monthlyTrend[0]?.month || data.periodMonth)} → ${formatMonthShort(data.periodMonth)}`}
              right={<ReconBadge ok={reconOk} text={reconOk ? 'Reconciled' : 'Drift detected'} />}
            />
            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-4">
              <TrendChart data={data.monthlyTrend} />
              <div className="flex gap-6 justify-center mt-3 text-xs text-gray-400">
                <LegendDot color="#8b5cf6" label="Melissa retained" />
                <LegendDot color="#f59e0b" label="Melissa (new)" />
                <LegendDot color="#10b981" label="Nathan" />
              </div>
            </div>
          </section>

          {/* Monthly table */}
          <section>
            <SectionHeading title="Monthly Book Value Per Bucket" />
            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Month</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Melissa Retained</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Melissa (New)</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Nathan</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-blue-400 bg-white/[0.02]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.monthlyTrend.map((m, idx) => {
                    const isCurrent = idx === data.monthlyTrend.length - 1;
                    return (
                      <tr
                        key={m.month}
                        className={isCurrent ? 'bg-blue-500/[0.06] border-t border-blue-500/30' : (idx % 2 === 1 ? 'bg-white/[0.015]' : '')}
                      >
                        <td className={`px-4 py-2.5 ${isCurrent ? 'text-white font-semibold' : 'text-gray-300'}`}>
                          {formatPeriod(m.month)}{isCurrent && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-blue-400">Current</span>}
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums ${isCurrent ? 'text-white font-semibold' : 'text-gray-300'}`}>
                          {formatCurrency(m.melissaRetainedCents)}
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums ${isCurrent ? 'text-white font-semibold' : 'text-gray-300'}`}>
                          {formatCurrency(m.melissaNewCents)}
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums ${isCurrent ? 'text-white font-semibold' : 'text-gray-300'}`}>
                          {formatCurrency(m.nathanCents)}
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums ${isCurrent ? 'text-blue-300 font-bold' : 'text-blue-400 font-medium'}`}>
                          {formatCurrency(m.visibleTotalCents)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Top loans per bucket */}
          <section>
            <SectionHeading
              title="Top Loans By Book Value"
              period={formatPeriod(data.periodMonth)}
            />
            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="flex gap-1 border-b border-white/[0.06] bg-white/[0.02] px-2 pt-2">
                {visibleBuckets.map((b) => (
                  <button
                    key={b.bucket}
                    onClick={() => setActiveBucketTab(b.bucket)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                      activeBucketTab === b.bucket
                        ? 'text-blue-400 border-blue-500'
                        : 'text-gray-400 border-transparent hover:text-white'
                    }`}
                  >
                    {b.displayName} <span className="text-xs text-gray-500 ml-1">({b.loanCount.toLocaleString('en-AU')})</span>
                  </button>
                ))}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Client</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Loan ID</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Lender</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Settled</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Book Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.loans
                    .filter((l) => l.bucket === activeBucketTab)
                    .sort((a, b) => b.loanBookCents - a.loanBookCents)
                    .slice(0, 15)
                    .map((l, i) => (
                      <tr key={l.loanId} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                        <td className="px-4 py-2.5 text-white">{l.client || <span className="text-gray-500 italic">—</span>}</td>
                        <td className="px-4 py-2.5 text-gray-400 font-mono text-xs">{l.loanId}</td>
                        <td className="px-4 py-2.5 text-gray-400">{l.lender || <span className="text-gray-500 italic">—</span>}</td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">{l.settlementDate || <span className="italic">—</span>}</td>
                        <td className="px-4 py-2.5 text-right text-white tabular-nums">{formatCurrency(l.loanBookCents)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <div className="px-4 py-2.5 text-xs text-gray-500 bg-white/[0.02] border-t border-white/[0.04]">
                Showing top 15 of {visibleBuckets.find((b) => b.bucket === activeBucketTab)?.loanCount.toLocaleString('en-AU')} loans in this bucket
              </div>
            </div>
          </section>

          {/* Reconciliation panel */}
          <section>
            <SectionHeading
              title="Reconciliation & Data Integrity"
              right={<ReconBadge ok={reconOk} text={reconOk ? 'Cents-exact match' : 'Drift detected'} />}
            />
            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <ReconCell label="Per-loan total" value={formatCurrency(data.reconciliation.perLoanTotalCents)} />
                <ReconCell label="Independent aggregate" value={formatCurrency(data.reconciliation.monthlyAggregateTotalCents)} />
                <ReconCell
                  label="Drift"
                  value={formatCurrency(data.reconciliation.driftCents)}
                  highlight={reconOk ? '#10b981' : '#f43f5e'}
                />
              </div>
              <p className="text-xs text-gray-500 italic">
                Cross-checks two independent sources (per-loan upload + monthly aggregate snapshot). A green badge requires both to tie within $1.00.
              </p>
              {unattributed && unattributed.loanCount > 0 && (
                <div className="mt-3 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-300">
                  <strong>Unattributed:</strong> {formatCurrency(unattributed.loanBookCents)} across {unattributed.loanCount} loan(s).
                  {unattributed.loanBookCents > data.reconciliation.unattributedThresholdCents
                    && ` Exceeds $${(data.reconciliation.unattributedThresholdCents / 100).toLocaleString('en-AU')} threshold.`}
                </div>
              )}
            </div>
          </section>

          {/* Footer */}
          <div className="pt-6 border-t border-white/[0.06] flex justify-between flex-wrap gap-3 text-xs text-gray-500">
            <div>
              Generated {new Date(data.generatedAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
              {' · '}Tenant: <span className="text-gray-400">{data.companyName}</span>
              {' · '}Ruleset: <span className="font-mono text-gray-400">{data.ruleSetHash.substring(0, 8)}…</span>
            </div>
            <div>Powered by <span className="text-gray-400 font-semibold">VALUTTEN</span></div>
          </div>

        </div>
      </div>
    </>
  );
};

// =============================================================================
// SUB-COMPONENTS (matching SharedBrokerReportPage primitives)
// =============================================================================

const SectionHeading: React.FC<{ title: string; period?: string; right?: React.ReactNode }> = ({
  title, period, right,
}) => (
  <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
    <div>
      <h2 className="text-base font-bold text-white tracking-tight">{title}</h2>
      {period && <p className="text-xs text-gray-500 mt-0.5">{period}</p>}
    </div>
    {right}
  </div>
);

const MetricCard: React.FC<{
  hero?: boolean;
  label: string;
  value: string;
  sub?: string;
  color: string;
}> = ({ hero, label, value, sub, color }) => (
  <div
    className={`bg-gray-800/50 border border-white/[0.06] rounded-xl p-4 ${hero ? 'ring-1 ring-blue-500/20' : ''}`}
    style={hero ? { boxShadow: `0 0 24px ${color}15` } : undefined}
  >
    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-2">{label}</div>
    <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

const ReconBadge: React.FC<{ ok: boolean; text: string }> = ({ ok, text }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider ${
      ok ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'
    }`}
  >
    {ok ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
    {text}
  </span>
);

const ReconCell: React.FC<{ label: string; value: string; highlight?: string }> = ({
  label, value, highlight,
}) => (
  <div className="bg-gray-900/40 border border-white/[0.04] rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">{label}</div>
    <div className="font-bold tabular-nums" style={highlight ? { color: highlight } : { color: 'white' }}>
      {value}
    </div>
  </div>
);

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span className="flex items-center gap-1.5">
    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
    {label}
  </span>
);

const TrendChart: React.FC<{ data: MonthlyBucketBreakdown[] }> = ({ data }) => {
  if (data.length === 0) return <div className="h-64 flex items-center justify-center text-gray-500">No data</div>;
  const maxTotal = Math.max(...data.map((d) => d.visibleTotalCents)) * 1.05;
  if (maxTotal <= 0) return <div className="h-64 flex items-center justify-center text-gray-500">No data</div>;

  const width = 1100, height = 280;
  const yTop = 20, yBottom = 240;
  const xLeft = 70, xRight = 1080;
  const xStep = (xRight - xLeft) / Math.max(1, data.length - 1);
  const y = (c: number) => yBottom - (c / maxTotal) * (yBottom - yTop);
  const xs = data.map((_, i) => xLeft + i * xStep);

  const retainedYs = data.map((d) => y(d.melissaRetainedCents));
  const melissaYs = data.map((d) => y(d.melissaRetainedCents + d.melissaNewCents));
  const nathanYs = data.map((d) => y(d.visibleTotalCents));

  const pathOf = (points: number[]) =>
    points.map((py, i) => `${i === 0 ? 'M' : 'L'} ${xs[i]},${py}`).join(' ') +
    ` L ${xs[xs.length - 1]},${yBottom} L ${xs[0]},${yBottom} Z`;

  const bandBetween = (topYs: number[], bottomYs: number[]) => {
    const topPath = topYs.map((py, i) => `${i === 0 ? 'M' : 'L'} ${xs[i]},${py}`).join(' ');
    const bottomPath = bottomYs
      .map((_, i) => `L ${xs[xs.length - 1 - i]},${bottomYs[bottomYs.length - 1 - i]}`)
      .join(' ');
    return `${topPath} ${bottomPath} Z`;
  };

  const formatYLabel = (cents: number) => {
    const d = cents / 100;
    if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(0)}M`;
    if (d >= 1_000) return `$${(d / 1_000).toFixed(0)}K`;
    return `$${d.toFixed(0)}`;
  };

  return (
    <div className="w-full h-72">
      <svg className="w-full h-full" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {[0, 0.25, 0.5, 0.75, 1].map((p) => (
          <line
            key={p}
            x1={xLeft}
            y1={yTop + (yBottom - yTop) * (1 - p)}
            x2={xRight}
            y2={yTop + (yBottom - yTop) * (1 - p)}
            stroke={p === 1 ? '#334155' : '#1e293b'}
            strokeWidth="1"
          />
        ))}
        {[0, 0.25, 0.5, 0.75, 1].map((p) => (
          <text
            key={p}
            x={xLeft - 5}
            y={yTop + (yBottom - yTop) * (1 - p) + 4}
            textAnchor="end"
            fontSize="10"
            fill="#64748b"
          >
            {formatYLabel(maxTotal * p)}
          </text>
        ))}
        <path d={pathOf(retainedYs)} fill="#8b5cf6" fillOpacity="0.7" />
        <path d={bandBetween(melissaYs, retainedYs)} fill="#f59e0b" fillOpacity="0.7" />
        <path d={bandBetween(nathanYs, melissaYs)} fill="#10b981" fillOpacity="0.7" />
        {data.map((d, i) => (
          <text key={d.month} x={xs[i]} y={yBottom + 18} textAnchor="middle" fontSize="10" fill="#64748b">
            {formatMonthShort(d.month)}
          </text>
        ))}
      </svg>
    </div>
  );
};

export default SharedBookValueReportPage;
